import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react'
import { Icon, type IconName } from '@components/atoms/Icon'
import { Typography, type TypographyVariant } from '@components/atoms/Typography'
import { Loader } from './Loader'
import styles from './Button.module.scss'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Visual variant. Maps to the Catalyst Mobile `Buttons/Button` `Type=…`
 * dimension (Figma node `3287:427074`).
 *   - `'primary'`      → brand fill (green); main CTA
 *   - `'secondary'`    → white surface + 1 px gray border
 *   - `'tertiary'`     → subtle gray surface, no border
 *   - `'link-color'`   → no chrome, blue text+icon
 *   - `'link-gray'`    → no chrome, neutral text+icon
 */
export type ButtonType =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'link-color'
  | 'link-gray'

/**
 * Container size. Affects height, padding, radius, and icon size.
 *   - `'md'` → 40 px tall, body-md/14
 *   - `'lg'` → 48 px tall, body-md/14 (link variants use body-lg/16)
 */
export type ButtonSize = 'sm' | 'md' | 'lg'

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'children'
>

export interface ButtonProps extends NativeButtonProps {
  /** Visual variant. Defaults to `'primary'`. */
  type?: ButtonType
  /** Container size. Defaults to `'md'`. */
  size?: ButtonSize
  /**
   * Switches the colour family to the error/destructive ramp. Works for
   * every `type`. See Figma `6218:85578`.
   */
  destructive?: boolean
  /** Leading icon (by registry name). Hidden while `loading`. */
  leadingIcon?: IconName
  /** Trailing icon (by registry name). Hidden while `loading`. */
  trailingIcon?: IconName
  /**
   * When true the button is disabled at the DOM level AND the content is
   * replaced with a centred spinner. The button announces `aria-busy=true`
   * so screen readers know to wait.
   */
  loading?: boolean
  /**
   * Native `<button>` semantic — useful for `<form>` submit triggers. The
   * prop is renamed to avoid colliding with the visual `type` prop above.
   * Defaults to `'button'`.
   */
  htmlType?: 'button' | 'submit' | 'reset'
  /** Stretches the button to its container's full width. */
  fullWidth?: boolean
  children?: ReactNode
}

// ---------------------------------------------------------------------------
// Internal: choose the Typography variant for the label
// ---------------------------------------------------------------------------
// Regular buttons (primary/secondary/tertiary) always use body-md, both
// sizes. Link variants follow the Figma rule: md → body-md, lg → body-lg.
const linkTypographyBySize: Record<ButtonSize, TypographyVariant> = {
  sm: 'label-sm',
  md: 'body-md',
  lg: 'body-lg',
}

function getTextVariant(type: ButtonType, size: ButtonSize): TypographyVariant {
  if (type === 'link-color' || type === 'link-gray') {
    return linkTypographyBySize[size]
  }
  return 'body-md'
}

// ---------------------------------------------------------------------------
// Internal: icon size per button size (matches the Figma `placeholder` cell)
// ---------------------------------------------------------------------------
function getIconSize(size: ButtonSize): number {
  return size === 'lg' ? 20 : size === 'md' ? 18 : 16
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    type = 'primary',
    size = 'md',
    destructive = false,
    leadingIcon,
    trailingIcon,
    loading = false,
    disabled = false,
    htmlType = 'button',
    fullWidth = false,
    children,
    className,
    'aria-label': ariaLabel,
    'aria-busy': ariaBusy,
    onClick,
    ...rest
  },
  ref,
) {
  const isDisabled = Boolean(disabled || loading)
  const iconSize = getIconSize(size)
  const textVariant = getTextVariant(type, size)

  const classes = [
    styles.root,
    styles[`type-${type}`],
    styles[`size-${size}`],
    destructive ? styles.destructive : null,
    fullWidth ? styles.fullWidth : null,
    loading ? styles.loading : null,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      {...rest}
      ref={ref}
      type={htmlType}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading ? true : ariaBusy}
      aria-label={ariaLabel}
      onClick={isDisabled ? undefined : onClick}
    >
      {/* Centred loader — absolute-positioned by SCSS so the button's
          width doesn't change while content is hidden. */}
      {loading && (
        <span className={styles.loaderSlot}>
          <Loader size={iconSize} />
        </span>
      )}

      {/* Actual content — hidden behind opacity:0 while loading so the
          button retains its resting width. */}
      <span className={styles.content} aria-hidden={loading || undefined}>
        {leadingIcon && (
          <Icon name={leadingIcon} size={iconSize} color="currentColor" />
        )}
        {children !== undefined && children !== null && children !== '' && (
          <Typography
            variant={textVariant}
            weight="medium"
            color="inherit"
            as="span"
            className={styles.label}
          >
            {children}
          </Typography>
        )}
        {trailingIcon && (
          <Icon name={trailingIcon} size={iconSize} color="currentColor" />
        )}
      </span>
    </button>
  )
})
